import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import axios from 'axios';
import OrderManagement from './OrderManagement';
import ProductionMonitoring from './ProductionMonitoring';
import MachineStatusGrid from './MachineStatusGrid';
import GatewayDownload from './GatewayDownload';
import { useAuth } from '../contexts/AuthContext';

const Dashboard = () => {
  const { isAdmin } = useAuth();
  const [machines, setMachines] = useState([]);
  const [loadingMachines, setLoadingMachines] = useState(true);
  const [machineError, setMachineError] = useState(null);

  useEffect(() => {
    loadMachines();
    // Refresh every 30 seconds
    const interval = setInterval(loadMachines, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadMachines = async () => {
    try {
      setLoadingMachines(true);
      setMachineError(null);
      const response = await axios.get('/api/machines/status');
      setMachines(response.data);
    } catch (error) {
      console.error('Error loading machines:', error);
      setMachineError('Errore nel caricamento stato macchine');
    } finally {
      setLoadingMachines(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Sistema di gestione ordini e monitoraggio produzione multi-macchina
      </Typography>

      <Grid container spacing={3}>
        {/* Sezione Stato Macchine */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>
              🏭 Stato Macchine
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Panoramica in tempo reale dello stato di tutte le macchine censite
            </Typography>

            {machineError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {machineError}
              </Alert>
            )}

            {loadingMachines && machines.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <MachineStatusGrid machines={machines} />
            )}
          </Paper>
        </Grid>

        {/* Divider */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
        </Grid>

        {/* Sezione Gestione Ordini */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>
              📋 Gestione Ordini
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Crea, modifica ed elimina ordini. Assegna ordini a macchine specifiche
              e inviali in produzione.
            </Typography>
            <OrderManagement />
          </Paper>
        </Grid>

        {/* Divider */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
        </Grid>

        {/* Sezione Monitoraggio Produzione */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>
              ⚙️ Monitoraggio Produzione
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Visualizza in tempo reale lo stato della macchina e il progresso degli ordini
              in produzione. I dati vengono aggiornati automaticamente tramite OPC UA.
            </Typography>
            <ProductionMonitoring />
          </Paper>
        </Grid>

        {/* Sezione Gateway OPC UA - solo admin (oggetto tecnico) */}
        {isAdmin() && (
          <>
            {/* Divider */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h5" gutterBottom>
                  🌐 Gateway OPC UA
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Scarica il pacchetto per installare un Edge Gateway su un PC dedicato nella rete di produzione.
                  Il gateway permette di connettere macchine OPC UA al sistema cloud.
                </Typography>
                <GatewayDownload />
              </Paper>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default Dashboard;